// react
import { useMemo } from 'react';
import styled from 'styled-components';

// components
import { Container, Input, ProgressBar, RadioGroup } from '@/components/atoms';
import {
  GenericFlowProps,
  OnboardingActions,
  OnboardingActionType,
  toGenericOnboarding,
} from '@/components/pages';

// theme
import { css } from 'styled-components';

/**
 * OnboardingDirectory renders the carousel onboarding views.
 */
export const OnboardingDirectory = toGenericOnboarding(
  ({
    config,
    currentIndex = 0,
    handleClick,
  }: GenericFlowProps<OnboardingDirectoryConfigItem>) => {
    return (
      <StyledContainer size='md'>
        <Wrapper>
          <ProgressBar limit={config.length} progress={currentIndex + 1} />
          <Body>
            <Title>{config[currentIndex]?.title}</Title>
            <Text>{config[currentIndex]?.text}</Text>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleClick?.(OnboardingActionType.Next);
              }}
            >
              {config[currentIndex]?.type === 'input' && (
                <Input
                  label='Location'
                  value={config[currentIndex]?.value}
                  onChange={config[currentIndex]?.onChange}
                />
              )}
            </form>
            {config[currentIndex]?.type === 'radio' && (
              <RadioGroup
                value={config[currentIndex]?.value}
                onChange={config[currentIndex]?.onChange}
                options={[
                  { label: 'Residential', value: 'residential' },
                  { label: 'Nursing', value: 'nursing' },
                  { label: 'Pallative', value: 'pallative' },
                  { label: 'Dementia', value: 'dementia' },
                  { label: 'Not sure', value: 'not-sure' },
                ]}
              />
            )}
          </Body>
        </Wrapper>
      </StyledContainer>
    );
  },
);

const StyledContainer = styled(Container)`
  height: auto;
  flex: 1;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: ${(props) => props.theme.spacing[64]};
  gap: ${(props) => props.theme.spacing[24]};

  ${(props) =>
    props.theme.breakpoint.md(css`
      align-items: center;
      gap: ${props.theme.spacing[64]};
    `)}
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[36]};
  width: 100%;
`;

const Title = styled.div`
  font: ${(props) => props.theme.typography.heading['3xl']};
  color: ${(props) => props.theme.color.primary[80]};
  width: 100%;

  ${(props) =>
    props.theme.breakpoint.md(css`
      font: ${props.theme.typography.heading['6xl']};
    `)}
`;

const Text = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};

  ${(props) =>
    props.theme.breakpoint.md(css`
      font: ${props.theme.typography.body.xl.regular};
    `)}
`;

export interface OnboardingDirectoryConfigItem extends OnboardingActions {
  id: string;
  title: string;
  text: string;
  type: 'input' | 'radio';
  value: string;
  onChange: (value: string) => void;
}

OnboardingDirectory.displayName = 'OnboardingDirectory';
export default toGenericOnboarding(OnboardingDirectory);
